<template>
  <div class="visualization-container">
    <div class="visualization-header">
      <span class="visualization-title">
        <font-awesome-icon :icon="['fas', 'chart-line']" class="visualization-icon" />
        {{ title || 'Visualization' }}
      </span>
      <div class="visualization-actions">
        <v-tooltip location="bottom" text="Toggle fullscreen">
          <template v-slot:activator="{ props }">
            <button
              v-bind="props"
              @click="toggleFullscreen"
              class="action-button"
              aria-label="Toggle fullscreen"
            >
              <Maximize2 v-if="!isFullscreen" :size="16" />
              <Minimize2 v-else :size="16" />
            </button>
          </template>
        </v-tooltip>
        <v-tooltip location="bottom" text="Copy code">
          <template v-slot:activator="{ props }">
            <button
              v-bind="props"
              @click="copyCode"
              class="action-button"
              aria-label="Copy visualization code"
            >
              <ClipboardCopy :size="16" />
            </button>
          </template>
        </v-tooltip>
        <v-tooltip location="bottom" text="Download HTML">
          <template v-slot:activator="{ props }">
            <button
              v-bind="props"
              @click="downloadHTML"
              class="action-button"
              aria-label="Download as HTML"
            >
              <Download :size="16" />
            </button>
          </template>
        </v-tooltip>
      </div>
    </div>
    
    <div 
      class="visualization-content" 
      :class="{ 'fullscreen': isFullscreen }"
      ref="contentContainer"
    >
      <iframe
        ref="visualizationFrame"
        :srcdoc="sanitizedContent"
        class="visualization-iframe"
        sandbox="allow-scripts allow-same-origin"
        @load="onIframeLoad"
      ></iframe>
      
      <div v-if="loading" class="loading-overlay">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>
    
    <div v-if="showCode" class="code-preview">
      <pre><code>{{ htmlContent }}</code></pre>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { Maximize2, Minimize2, ClipboardCopy, Download } from 'lucide-vue-next';
// DOMPurify is provided by vue-dompurify-html
// We'll use a different approach for sanitization

export default {
  name: 'VisualizationRenderer',
  components: {
    Maximize2,
    Minimize2,
    ClipboardCopy,
    Download
  },
  props: {
    htmlContent: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    isDarkTheme: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const visualizationFrame = ref(null);
    const contentContainer = ref(null);
    const isFullscreen = ref(false);
    const showCode = ref(false);
    const loading = ref(true);

    // Since we're using iframe with srcdoc, we can pass the content directly
    // The iframe sandbox provides security isolation
    const sanitizedContent = computed(() => {
      // Add dark theme styles if needed
      let content = props.htmlContent;
      if (props.isDarkTheme) {
        content = injectDarkThemeStyles(content);
      }
      
      // Basic sanitization - remove obvious script injection attempts
      // The iframe sandbox will provide the main security
      return content
        .replace(/<script[^>]*>alert\s*\(/gi, '<script>// alert(')
        .replace(/on\w+\s*=\s*["']?alert\s*\(/gi, 'data-blocked="alert(');
    });

    const injectDarkThemeStyles = (html) => {
      // Add dark theme CSS to the HTML
      const darkThemeCSS = `
        <style>
          body {
            background-color: #1a1a1a;
            color: #e0e0e0;
          }
          .visualization-container {
            background-color: #2F3033;
            color: #FFFFFF;
          }
        </style>
      `;
      
      // Insert the dark theme CSS before the closing head tag or at the beginning
      if (html.includes('</head>')) {
        return html.replace('</head>', darkThemeCSS + '</head>');
      } else if (html.includes('<body>')) {
        return html.replace('<body>', '<body>' + darkThemeCSS);
      } else {
        return darkThemeCSS + html;
      }
    };

    const onIframeLoad = () => {
      loading.value = false;
      adjustIframeHeight();
    };

    const adjustIframeHeight = () => {
      if (visualizationFrame.value && visualizationFrame.value.contentWindow) {
        try {
          const doc = visualizationFrame.value.contentDocument || visualizationFrame.value.contentWindow.document;
          const height = doc.body.scrollHeight;
          visualizationFrame.value.style.height = `${height + 20}px`;
        } catch (e) {
          console.warn('Could not adjust iframe height:', e);
          visualizationFrame.value.style.height = '600px'; // Default height
        }
      }
    };

    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      if (isFullscreen.value) {
        emit('fullscreen-enter');
      } else {
        emit('fullscreen-exit');
      }
    };

    const copyCode = async () => {
      try {
        await navigator.clipboard.writeText(props.htmlContent);
        emit('code-copied');
        // You could add a toast notification here
        console.log('Code copied to clipboard');
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    };

    const downloadHTML = () => {
      const blob = new Blob([props.htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${props.title || 'visualization'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      emit('downloaded');
    };

    // Watch for content changes
    watch(() => props.htmlContent, () => {
      loading.value = true;
    });

    // Handle escape key for fullscreen
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isFullscreen.value) {
        toggleFullscreen();
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', handleEscKey);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleEscKey);
    });

    return {
      visualizationFrame,
      contentContainer,
      isFullscreen,
      showCode,
      loading,
      sanitizedContent,
      onIframeLoad,
      toggleFullscreen,
      copyCode,
      downloadHTML
    };
  }
};
</script>

<style scoped>
.visualization-container {
  margin: 10px 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
}

.dark-theme .visualization-container {
  border-color: #444;
  background-color: #2F3033;
}

.visualization-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.dark-theme .visualization-header {
  background-color: #1a1a1a;
  border-bottom-color: #444;
}

.visualization-title {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
}

.dark-theme .visualization-title {
  color: #e0e0e0;
}

.visualization-icon {
  color: #667eea;
}

.visualization-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #333;
}

.dark-theme .action-button {
  color: #aaa;
}

.dark-theme .action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.visualization-content {
  position: relative;
  width: 100%;
  min-height: 400px;
  background-color: #fff;
}

.dark-theme .visualization-content {
  background-color: #1a1a1a;
}

.visualization-content.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background-color: #fff;
}

.dark-theme .visualization-content.fullscreen {
  background-color: #1a1a1a;
}

.visualization-iframe {
  width: 100%;
  height: 100%;
  border: none;
  min-height: 400px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
}

.dark-theme .loading-overlay {
  background-color: rgba(0, 0, 0, 0.8);
}

.code-preview {
  padding: 16px;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  max-height: 300px;
  overflow-y: auto;
}

.dark-theme .code-preview {
  background-color: #1a1a1a;
  border-top-color: #444;
}

.code-preview pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.code-preview code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  color: #333;
}

.dark-theme .code-preview code {
  color: #e0e0e0;
}
</style>
